import type { DeploymentResponse, DeviceResponseV5 } from '@emma/api_client/types';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Icon,
  InlineNotification,
  Modal,
  Stack,
  Table,
  Tag,
  Text,
  useDisclosure,
  useToast,
} from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TextHeader } from '@/components/common';
import { useDevices } from '@/hooks/device';
import {
  useBatchCreateRolloutWindows,
  useBatchDeleteRolloutWindowsByTag,
} from '@/hooks/rollout_windows';
import type { FleetRolloutWindowStats } from '@/hooks/rollout_windows/fleet_rollout_window_stats';
import type { RolloutWindowResponse } from '@/hooks/rollout_windows/rollout_windows';
import { useCanWriteById } from '@/hooks/workspace';
import { ToastOptions } from '@/utility/ToastOptions';
import {
  clearPendingSubmission,
  loadPendingSubmission,
  type PendingSubmission,
  type PlannedRolloutWindow,
  savePendingSubmission,
} from './pending_submission';
import type { RolloutWave } from './types';
import { WaveEditor } from './WaveEditor';

type Props = {
  deployment: DeploymentResponse;
  workspaceId: number;
  fleetId: number;
  rolloutStats: FleetRolloutWindowStats;
};

/** API limits batch rollout window creation to 100 items per request. */
const ROLLOUT_WINDOW_BATCH_SIZE = 100;

const createDefaultWave = (): RolloutWave => ({
  id: crypto.randomUUID(),
  schedule: { kind: 'oneTime', startTime: '' },
  durationInSeconds: undefined,
  targetingMode: 'percentage',
  percentage: 100,
  deviceIds: [],
  type: 'APPLICATION',
});

const validateWaveSchedule = (wave: RolloutWave, label: string): string[] => {
  if (!wave.schedule.startTime) {
    return [`${label}: Start time is required.`];
  }
  const parsed = new Date(wave.schedule.startTime);
  if (Number.isNaN(parsed.getTime())) {
    return [`${label}: Start time is not a valid date.`];
  }
  return [];
};

const validateWave = (wave: RolloutWave, index: number, totalDeviceCount: number): string[] => {
  const label = `Wave ${index + 1}`;
  const errors: string[] = [];
  if (!wave.durationInSeconds || wave.durationInSeconds <= 0) {
    errors.push(`${label}: Duration must be greater than 0.`);
  }
  errors.push(...validateWaveSchedule(wave, label));
  if (wave.targetingMode === 'specific' && wave.deviceIds.length === 0) {
    errors.push(`${label}: At least one device must be selected.`);
  }
  if (wave.targetingMode === 'percentage' && totalDeviceCount === 0) {
    errors.push(`${label}: No devices in fleet to target by percentage.`);
  }
  return errors;
};

const validatePlan = (waves: RolloutWave[], totalDeviceCount: number): string[] => {
  const errors = waves.flatMap((wave, i) => validateWave(wave, i, totalDeviceCount));
  const percentageWaves = waves.filter((w) => w.targetingMode === 'percentage');
  const totalPercent = percentageWaves.reduce((sum, w) => sum + w.percentage, 0);
  if (percentageWaves.length > 0 && totalPercent > 100) {
    errors.push(`Total percentage across waves is ${totalPercent}% — this exceeds 100%.`);
  }

  const specificWaves = waves.filter((w) => w.targetingMode === 'specific');
  const seenDeviceIds = new Set<number>();
  for (const wave of specificWaves) {
    for (const id of wave.deviceIds) {
      if (seenDeviceIds.has(id)) {
        errors.push(
          'The same device is selected in multiple waves. Each device should only appear in one wave.'
        );
        return errors;
      }
      seenDeviceIds.add(id);
    }
  }

  return errors;
};

const percentageBannerVariant = (total: number): 'error' | 'success' | 'warning' => {
  if (total > 100) return 'error';
  if (total === 100) return 'success';
  return 'warning';
};

const percentageBannerMessage = (total: number): { title: string; description: string } => {
  if (total > 100) {
    return {
      title: 'Exceeds 100%',
      description: `Total percentage across waves is ${total}% — reduce to 100%.`,
    };
  }
  if (total === 100) {
    return {
      title: 'Full coverage',
      description: 'All fleet devices are covered across percentage-based waves.',
    };
  }
  return {
    title: 'Partial coverage',
    description: `Total percentage across waves is ${total}%. The remaining ${100 - total}% of devices will not be covered by percentage-based waves (specific-device waves are counted separately).`,
  };
};

const PercentageBanner = ({ total }: { total: number }) => {
  const variant = percentageBannerVariant(total);
  const { title, description } = percentageBannerMessage(total);
  return (
    <InlineNotification mb={4} variant={variant}>
      <InlineNotification.Content>
        <InlineNotification.Title>{title}</InlineNotification.Title>
        <InlineNotification.Description>{description}</InlineNotification.Description>
      </InlineNotification.Content>
    </InlineNotification>
  );
};

const PendingSubmissionNotice = ({
  canCancel,
  isCancelling,
  isPending,
  onCancel,
}: {
  canCancel: boolean;
  isCancelling: boolean;
  isPending: boolean;
  onCancel: () => void;
}) => {
  if (!isPending) return null;

  return (
    <InlineNotification mb={4} variant="warning">
      <InlineNotification.Content>
        <InlineNotification.Title>Rollout plan submission interrupted</InlineNotification.Title>
        <InlineNotification.Description>
          Retry the saved plan to create only rollout windows that are still missing, or cancel it
          before making changes.
        </InlineNotification.Description>
        <Button
          isDisabled={!canCancel}
          isLoading={isCancelling}
          mt={3}
          onClick={onCancel}
          size="sm"
          variant="outline"
        >
          Cancel pending plan
        </Button>
      </InlineNotification.Content>
    </InlineNotification>
  );
};

const PendingSubmissionSummary = ({
  fleetDevices,
  submission,
}: {
  fleetDevices: DeviceResponseV5[];
  submission: PendingSubmission;
}) => {
  const groups = new Map<string, PlannedRolloutWindow[]>();
  for (const rolloutWindow of submission.rolloutWindows) {
    const key = JSON.stringify([
      rolloutWindow.startTime,
      rolloutWindow.durationInSeconds,
      rolloutWindow.type,
    ]);
    groups.set(key, [...(groups.get(key) ?? []), rolloutWindow]);
  }

  return (
    <Stack spacing={4}>
      {Array.from(groups.values()).map((windows, index) => {
        const first = windows[0];
        return (
          <Box
            borderColor="gray.200"
            borderRadius="md"
            borderWidth="1px"
            key={first.idempotencyKey}
            p={3}
          >
            <Text fontWeight="semibold" mb={2}>
              Saved wave {index + 1}
            </Text>
            <Table size="sm" variant="simple">
              <Table.Body>
                <Table.Row>
                  <Table.Cell color="gray.500" fontWeight="medium" width="140px">
                    Schedule
                  </Table.Cell>
                  <Table.Cell>{new Date(first.startTime).toLocaleString()}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell color="gray.500" fontWeight="medium">
                    Duration
                  </Table.Cell>
                  <Table.Cell>{first.durationInSeconds}s</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell color="gray.500" fontWeight="medium">
                    Update type
                  </Table.Cell>
                  <Table.Cell>
                    {first.type === 'APPLICATION_AND_OS'
                      ? 'Application and OS'
                      : 'Application only'}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell color="gray.500" fontWeight="medium">
                    Devices
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap={1} wrap="wrap">
                      {windows.map(({ deviceId, idempotencyKey }) => (
                        <Tag colorScheme="teal" key={idempotencyKey} size="sm">
                          {fleetDevices.find((device) => device.id === deviceId)?.name ??
                            `Device #${deviceId}`}
                        </Tag>
                      ))}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Box>
        );
      })}
    </Stack>
  );
};

const DeploymentStatusTags = ({
  failed,
  isLoading,
  pending,
  succeeded,
  total,
}: {
  failed: number;
  isLoading: boolean;
  pending: number;
  succeeded: number;
  total: number;
}) => {
  if (isLoading || total === 0) return null;

  return (
    <Flex gap={3} mb={4}>
      <Tag colorScheme="green" size="md">
        Deployed: {succeeded}/{total}
      </Tag>
      {failed > 0 && (
        <Tag colorScheme="red" size="md">
          Failed: {failed}/{total}
        </Tag>
      )}
      {pending > 0 && (
        <Tag colorScheme="gray" size="md">
          Pending: {pending}/{total}
        </Tag>
      )}
    </Flex>
  );
};

type BatchDeleteRolloutWindows = ReturnType<typeof useBatchDeleteRolloutWindowsByTag>;
type BatchDeleteResult = NonNullable<Awaited<ReturnType<BatchDeleteRolloutWindows>>>;
type Toast = ReturnType<typeof useToast>;

const toastCancelResult = (result: BatchDeleteResult, toast: Toast): boolean => {
  if (result.totalFailed === 0) {
    toast(
      ToastOptions.success(
        'Rollout plan cancelled',
        `Deleted rollout windows for ${result.totalDeleted} device(s).`
      )
    );
    return true;
  }

  const maxDisplayed = 5;
  const failedDevices = result.failed
    .slice(0, maxDisplayed)
    .map((failure) => `Device ${failure.deviceId}: ${failure.error}`)
    .join('\n');
  const suffix =
    result.totalFailed > maxDisplayed ? `\n...and ${result.totalFailed - maxDisplayed} more` : '';
  const title = result.totalDeleted > 0 ? 'Partial failure' : 'Cancel failed';
  const description =
    result.totalDeleted > 0
      ? `Deleted ${result.totalDeleted} window(s), but ${result.totalFailed} device(s) failed:\n${failedDevices}${suffix}`
      : `Failed to delete rollout windows for ${result.totalFailed} device(s):\n${failedDevices}${suffix}`;
  toast(ToastOptions.error(title, description));
  return false;
};

const executeCancelPlan = async ({
  batchDeleteRolloutWindows,
  clearSavedSubmission,
  loadStats,
  pendingTag,
  tag,
  toast,
}: {
  batchDeleteRolloutWindows: BatchDeleteRolloutWindows;
  clearSavedSubmission: () => void;
  loadStats: FleetRolloutWindowStats['loadStats'];
  pendingTag?: string;
  tag: string;
  toast: Toast;
}): Promise<boolean> => {
  let result: Awaited<ReturnType<BatchDeleteRolloutWindows>>;
  try {
    result = await batchDeleteRolloutWindows({ tag });
  } catch {
    return false;
  } finally {
    loadStats();
  }
  if (!result || !toastCancelResult(result, toast)) return false;
  if (pendingTag !== tag) return true;

  try {
    clearSavedSubmission();
    return true;
  } catch (error) {
    toast(
      ToastOptions.error(
        'Rollout plan cancelled',
        `The windows were deleted, but the saved retry state could not be cleared: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    );
    return false;
  }
};

const prepareSubmission = ({
  buildRolloutWindows,
  deploymentId,
  fleetId,
  pendingSubmission,
  workspaceId,
}: {
  buildRolloutWindows: () => PlannedRolloutWindow[];
  deploymentId: number;
  fleetId: number;
  pendingSubmission?: PendingSubmission;
  workspaceId: number;
}): PendingSubmission => {
  if (pendingSubmission) return pendingSubmission;

  const submission = {
    tag: `deployment-${deploymentId}-${Date.now()}`,
    rolloutWindows: buildRolloutWindows(),
  };
  savePendingSubmission(workspaceId, fleetId, deploymentId, submission);
  return submission;
};

const finishSuccessfulSubmission = ({
  clearSavedSubmission,
  toast,
  totalFailed,
}: {
  clearSavedSubmission: () => void;
  toast: Toast;
  totalFailed: number;
}): boolean => {
  if (totalFailed > 0) return false;

  try {
    clearSavedSubmission();
    return true;
  } catch (error) {
    toast(
      ToastOptions.error(
        'Rollout plan applied',
        `The rollout windows were applied, but the saved retry state could not be cleared: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    );
    return false;
  }
};

export const GradualRolloutPlanner = ({
  deployment,
  workspaceId,
  fleetId,
  rolloutStats,
}: Props) => {
  const [waves, setWaves] = useState<RolloutWave[]>([createDefaultWave()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancellingPending, setIsCancellingPending] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<PendingSubmission | undefined>(() =>
    loadPendingSubmission(workspaceId, fleetId, deployment.id)
  );
  const [submitted, setSubmitted] = useState(false);
  const toast = useToast();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const canWrite = useCanWriteById(workspaceId);

  // Fetch all devices in this fleet (limit 1000 covers expected fleet sizes; API default is 100).
  const { data: devicesData, isLoading: isLoadingDevices } = useDevices({
    fleetIdIn: [fleetId],
    workspaceIdIn: [workspaceId],
    limit: 1000,
  });

  const fleetDevices = devicesData?.devices ?? [];

  const batchCreateRolloutWindows = useBatchCreateRolloutWindows(workspaceId);
  const batchDeleteRolloutWindows = useBatchDeleteRolloutWindowsByTag(workspaceId);

  const { deviceSummaries, loadStats } = rolloutStats;

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    setPendingSubmission(loadPendingSubmission(workspaceId, fleetId, deployment.id));
  }, [deployment.id, fleetId, workspaceId]);

  const tagPrefix = `deployment-${deployment.id}-`;

  const existingPlans = useMemo(() => {
    const planMap = new Map<string, RolloutWindowResponse[]>();
    for (const summary of deviceSummaries) {
      for (const rw of summary.rolloutWindows) {
        if (rw.tag?.startsWith(tagPrefix)) {
          const existing = planMap.get(rw.tag) ?? [];
          existing.push(rw);
          planMap.set(rw.tag, existing);
        }
      }
    }
    return Array.from(planMap.entries()).map(([tag, windows]) => ({ tag, windows }));
  }, [deviceSummaries, tagPrefix]);

  const clearSavedSubmission = () => {
    clearPendingSubmission(workspaceId, fleetId, deployment.id);
    setPendingSubmission(undefined);
  };

  const handleCancelPlan = (tag: string): Promise<boolean> =>
    executeCancelPlan({
      batchDeleteRolloutWindows,
      clearSavedSubmission,
      loadStats,
      pendingTag: pendingSubmission?.tag,
      tag,
      toast,
    });

  const handleCancelPendingPlan = async () => {
    if (!pendingSubmission) return;

    setIsCancellingPending(true);
    try {
      await handleCancelPlan(pendingSubmission.tag);
    } finally {
      setIsCancellingPending(false);
    }
  };
  const handleAddWave = () => {
    setWaves((prev) => {
      const percentageWaves = prev.filter((w) => w.targetingMode === 'percentage');
      const usedPercent = percentageWaves.reduce((sum, w) => sum + w.percentage, 0);

      if (usedPercent >= 100 && percentageWaves.length > 0) {
        // Shrink the last percentage wave to make room for the new one
        const newWavePercent = Math.min(
          10,
          Math.max(1, Math.floor(100 / (percentageWaves.length + 1)))
        );
        const lastPercentageWave = percentageWaves[percentageWaves.length - 1];
        const updatedPrev = prev.map((w) =>
          w.id === lastPercentageWave.id
            ? { ...w, percentage: Math.max(1, w.percentage - newWavePercent) }
            : w
        );
        return [...updatedPrev, { ...createDefaultWave(), percentage: newWavePercent }];
      }

      const remaining = Math.max(1, 100 - usedPercent);
      return [...prev, { ...createDefaultWave(), percentage: remaining }];
    });
  };

  const handleRemoveWave = (id: string) => {
    setWaves((prev) => prev.filter((w) => w.id !== id));
  };

  const handleWaveChange = useCallback((updated: RolloutWave) => {
    setWaves((prev) => {
      const newWaves = prev.map((w) => (w.id === updated.id ? updated : w));
      // Clamp other percentage waves so total never exceeds 100
      if (updated.targetingMode === 'percentage') {
        const othersSum = newWaves
          .filter((w) => w.id !== updated.id && w.targetingMode === 'percentage')
          .reduce((s, w) => s + w.percentage, 0);
        const budget = 100 - updated.percentage;
        if (othersSum > budget) {
          // Proportionally scale down other percentage waves
          const scale = budget / othersSum;
          return newWaves.map((w) =>
            w.id !== updated.id && w.targetingMode === 'percentage'
              ? { ...w, percentage: Math.max(1, Math.round(w.percentage * scale)) }
              : w
          );
        }
      }
      return newWaves;
    });
  }, []);

  const resolveDeviceIdsPerWave = (): Map<string, number[]> => {
    const result = new Map<string, number[]>();

    // Collect all device IDs claimed by specific-targeting waves
    const specificDeviceIds = new Set(
      waves.filter((w) => w.targetingMode === 'specific').flatMap((w) => w.deviceIds)
    );

    // Build pool excluding specifically targeted devices
    const percentagePool = fleetDevices.filter((d) => !specificDeviceIds.has(d.id));
    let offset = 0;

    for (const wave of waves) {
      if (wave.targetingMode === 'specific') {
        result.set(wave.id, wave.deviceIds);
      } else {
        const remaining = percentagePool.length - offset;
        const count = Math.min(
          remaining,
          Math.max(1, Math.ceil((wave.percentage / 100) * percentagePool.length))
        );
        const ids = percentagePool.slice(offset, offset + count).map((d) => d.id);
        result.set(wave.id, ids);
        offset += count;
      }
    }

    return result;
  };

  const handleSubmit = () => {
    if (pendingSubmission) {
      onConfirmOpen();
      return;
    }

    const errors = validatePlan(waves, fleetDevices.length);
    if (errors.length > 0) {
      toast(ToastOptions.error('Validation failed', errors.join(' ')));
      return;
    }
    onConfirmOpen();
  };

  const buildRolloutWindows = (): PlannedRolloutWindow[] => {
    const rolloutWindows: PlannedRolloutWindow[] = [];

    const deviceIdsPerWave = resolveDeviceIdsPerWave();

    for (const wave of waves) {
      const deviceIds = deviceIdsPerWave.get(wave.id) ?? [];
      const startTime = new Date(wave.schedule.startTime).toISOString();

      for (const deviceId of deviceIds) {
        rolloutWindows.push({
          deviceId,
          durationInSeconds: wave.durationInSeconds!,
          idempotencyKey: crypto.randomUUID(),
          startTime,
          type: wave.type,
        });
      }
    }

    return rolloutWindows;
  };

  const submitRolloutWindowBatches = async (
    tag: string,
    rolloutWindows: ReturnType<typeof buildRolloutWindows>
  ): Promise<
    | {
        totalCreated: number;
        totalReplayed: number;
        totalFailed: number;
        allFailed: { deviceId: number; error: string }[];
      }
    | undefined
  > => {
    let totalCreated = 0;
    let totalReplayed = 0;
    let totalFailed = 0;
    const allFailed: { deviceId: number; error: string }[] = [];

    for (let i = 0; i < rolloutWindows.length; i += ROLLOUT_WINDOW_BATCH_SIZE) {
      const chunk = rolloutWindows.slice(i, i + ROLLOUT_WINDOW_BATCH_SIZE);
      let result: Awaited<ReturnType<typeof batchCreateRolloutWindows>>;
      try {
        result = await batchCreateRolloutWindows({ tag, rolloutWindows: chunk });
      } catch {
        return undefined;
      }

      totalCreated += result.totalCreated;
      totalReplayed += result.totalReplayed;
      totalFailed += result.totalFailed;
      allFailed.push(...result.failed);
    }

    return { totalCreated, totalReplayed, totalFailed, allFailed };
  };

  const toastCreateResult = (
    totalCreated: number,
    totalReplayed: number,
    totalFailed: number,
    allFailed: { deviceId: number; error: string }[],
    batchCount: number
  ) => {
    const totalApplied = totalCreated + totalReplayed;
    if (totalFailed === 0) {
      const plural = totalApplied !== 1 ? 's' : '';
      const replayDescription =
        totalReplayed > 0
          ? ` (${totalCreated} newly created, ${totalReplayed} already existed)`
          : '';
      toast(
        ToastOptions.success(
          'Rollout plan applied',
          `Applied ${totalApplied} rollout window${plural}${replayDescription} across ${batchCount} batch(es).`
        )
      );
      return;
    }

    const maxDisplayed = 5;
    const failedDevices = allFailed
      .slice(0, maxDisplayed)
      .map((f) => `Device ${f.deviceId}: ${f.error}`)
      .join('\n');
    const suffix = totalFailed > maxDisplayed ? `\n...and ${totalFailed - maxDisplayed} more` : '';

    if (totalApplied > 0) {
      toast(
        ToastOptions.error(
          'Partial failure',
          `Applied ${totalApplied} window(s) (${totalCreated} newly created, ${totalReplayed} already existed), but ${totalFailed} device(s) failed:\n${failedDevices}${suffix}`
        )
      );
    } else {
      toast(
        ToastOptions.error(
          'Rollout plan failed',
          `Failed to create rollout windows for ${totalFailed} device(s):\n${failedDevices}${suffix}`
        )
      );
    }
  };

  const handleConfirmSubmit = async () => {
    onConfirmClose();
    setIsSubmitting(true);

    let submission: PendingSubmission;
    try {
      submission = prepareSubmission({
        buildRolloutWindows,
        deploymentId: deployment.id,
        fleetId,
        pendingSubmission,
        workspaceId,
      });
      setPendingSubmission(submission);
    } catch (error) {
      toast(
        ToastOptions.error(
          'Rollout plan not submitted',
          `The retry state could not be saved: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        )
      );
      setIsSubmitting(false);
      return;
    }
    const batchCount = Math.ceil(submission.rolloutWindows.length / ROLLOUT_WINDOW_BATCH_SIZE);

    try {
      const batchResult = await submitRolloutWindowBatches(
        submission.tag,
        submission.rolloutWindows
      );
      if (batchResult === undefined) {
        return;
      }

      const { totalCreated, totalReplayed, totalFailed, allFailed } = batchResult;
      toastCreateResult(totalCreated, totalReplayed, totalFailed, allFailed, batchCount);

      if (finishSuccessfulSubmission({ clearSavedSubmission, toast, totalFailed })) {
        setSubmitted(true);
      }
    } catch (error) {
      toast(
        ToastOptions.error(
          'Rollout submission interrupted',
          error instanceof Error ? error.message : 'Unexpected error'
        )
      );
    } finally {
      loadStats();
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (pendingSubmission) return;
    setWaves([createDefaultWave()]);
    setSubmitted(false);
  };

  const percentageWaves = waves.filter((w) => w.targetingMode === 'percentage');
  const hasPercentageWaves = percentageWaves.length > 0;
  const percentageTotal = percentageWaves.reduce((sum, w, _i, arr) => {
    const othersRaw = arr.filter((o) => o.id !== w.id).reduce((s, o) => s + o.percentage, 0);
    const max = Math.max(1, 100 - othersRaw);
    return sum + Math.min(w.percentage, max);
  }, 0);

  const deploymentStats = useMemo(() => {
    let succeeded = 0;
    let failed = 0;
    let pending = 0;
    for (const device of fleetDevices) {
      const dep = device.deployments?.find((d) => d.deploymentId === deployment.id && d.isActive);
      if (!dep) {
        pending++;
      } else if (dep.latestState === 'SUCCEEDED') {
        succeeded++;
      } else {
        failed++;
      }
    }
    return { succeeded, failed, pending, total: fleetDevices.length };
  }, [fleetDevices, deployment.id]);

  if (submitted) {
    return (
      <Box background="white" boxShadow="md" p={6} rounded="md" width="100%">
        <InlineNotification variant="success">
          <InlineNotification.Content>
            <InlineNotification.Title>Rollout plan applied</InlineNotification.Title>
            <InlineNotification.Description>
              Rollout windows have been created for all targeted devices.
            </InlineNotification.Description>
          </InlineNotification.Content>
        </InlineNotification>
        <Button mt={4} onClick={handleReset} variant="outline">
          Configure another plan
        </Button>
      </Box>
    );
  }

  return (
    <Box background="white" boxShadow="md" p={6} rounded="md" width="100%">
      <TextHeader mb={2} tagLevel="h3" title="Gradual Rollout Plan" />
      <Text color="gray.600" fontSize="sm" mb={4}>
        Define waves to roll out deployment #{deployment.id} gradually across your fleet. Each wave
        targets a subset of devices — either a percentage of the fleet or specific devices — and
        fires at its own scheduled time. All waves are submitted as a single batch.
      </Text>

      <DeploymentStatusTags isLoading={isLoadingDevices} {...deploymentStats} />

      {hasPercentageWaves && <PercentageBanner total={percentageTotal} />}

      <PendingSubmissionNotice
        canCancel={canWrite && !isSubmitting}
        isCancelling={isCancellingPending}
        isPending={pendingSubmission !== undefined}
        onCancel={() => void handleCancelPendingPlan()}
      />

      {existingPlans.length > 0 && (
        <Box borderColor="gray.200" borderRadius="md" borderWidth="1px" mb={4} p={4}>
          <Text fontWeight="semibold" mb={2}>
            Active Rollout Plans
          </Text>
          <Stack spacing={3}>
            {existingPlans.map(({ tag, windows }) => {
              const activeCount = windows.filter((w) => w.status === 'ACTIVE').length;
              const timestamp = tag.replace(tagPrefix, '');
              const createdAt = new Date(Number(timestamp));
              const label = Number.isNaN(createdAt.getTime())
                ? tag
                : `Created ${createdAt.toLocaleString()}`;
              return (
                <Flex align="center" justify="space-between" key={tag}>
                  <Box>
                    <Text fontSize="sm">{label}</Text>
                    <Text color="gray.500" fontSize="xs">
                      {windows.length} window{windows.length !== 1 ? 's' : ''} ({activeCount}{' '}
                      active)
                    </Text>
                  </Box>
                  <Button
                    colorScheme="red"
                    isDisabled={!canWrite}
                    onClick={() => void handleCancelPlan(tag)}
                    size="sm"
                    variant="outline"
                  >
                    Cancel plan
                  </Button>
                </Flex>
              );
            })}
          </Stack>
        </Box>
      )}

      <Stack direction="column" spacing={4}>
        {waves.map((wave, index) => {
          const otherPercentageTotal = waves
            .filter((w) => w.id !== wave.id && w.targetingMode === 'percentage')
            .reduce((sum, w) => sum + w.percentage, 0);
          const maxPercentage = Math.max(1, 100 - otherPercentageTotal);
          return (
            <WaveEditor
              availableDevices={fleetDevices}
              canRemove={waves.length > 1}
              isDisabled={pendingSubmission !== undefined}
              isLoadingDevices={isLoadingDevices}
              key={wave.id}
              maxPercentage={maxPercentage}
              onChange={handleWaveChange}
              onRemove={() => handleRemoveWave(wave.id)}
              wave={wave}
              waveNumber={index + 1}
            />
          );
        })}
      </Stack>

      <Divider my={4} />

      <Flex align="center" justify="space-between">
        <Button
          isDisabled={pendingSubmission !== undefined}
          leftIcon={<Icon as={PlusBold} />}
          onClick={handleAddWave}
          size="sm"
          variant="outline"
        >
          Add wave
        </Button>

        <Flex align="center" gap={4}>
          {hasPercentageWaves && (
            <Tag
              colorScheme={
                percentageTotal > 100 ? 'red' : percentageTotal === 100 ? 'green' : 'yellow'
              }
              size="md"
            >
              Total: {percentageTotal}%
            </Tag>
          )}
          <ButtonGroup>
            <Button
              isDisabled={percentageTotal > 100 || !canWrite || isCancellingPending}
              isLoading={isSubmitting}
              onClick={handleSubmit}
            >
              {pendingSubmission
                ? 'Retry rollout plan'
                : `Apply rollout plan (${waves.length} wave${waves.length !== 1 ? 's' : ''})`}
            </Button>
            <Button
              isDisabled={isSubmitting || pendingSubmission !== undefined}
              onClick={handleReset}
              variant="ghost"
            >
              Reset
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>

      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} size="xl">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>Confirm Rollout Plan</Modal.Header>
          <Modal.CloseButton />
          <Modal.Body>
            <Text fontSize="sm" mb={4}>
              {pendingSubmission
                ? `The saved rollout windows below will be retried for deployment #${deployment.id}:`
                : `The following rollout windows will be created for deployment #${deployment.id}:`}
            </Text>
            {pendingSubmission ? (
              <PendingSubmissionSummary
                fleetDevices={fleetDevices}
                submission={pendingSubmission}
              />
            ) : (
              <Stack spacing={4}>
                {waves.map((wave, index) => {
                  const deviceIds = resolveDeviceIdsPerWave().get(wave.id) ?? [];
                  return (
                    <Box
                      borderColor="gray.200"
                      borderRadius="md"
                      borderWidth="1px"
                      key={wave.id}
                      p={3}
                    >
                      <Text fontWeight="semibold" mb={2}>
                        Wave {index + 1}
                      </Text>
                      <Table size="sm" variant="simple">
                        <Table.Body>
                          <Table.Row>
                            <Table.Cell color="gray.500" fontWeight="medium" width="140px">
                              Schedule
                            </Table.Cell>
                            <Table.Cell>
                              {new Date(wave.schedule.startTime).toLocaleString()}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell color="gray.500" fontWeight="medium">
                              Duration
                            </Table.Cell>
                            <Table.Cell>{wave.durationInSeconds}s</Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell color="gray.500" fontWeight="medium">
                              Update type
                            </Table.Cell>
                            <Table.Cell>
                              {wave.type === 'APPLICATION'
                                ? 'Application only'
                                : 'Application and OS'}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell color="gray.500" fontWeight="medium">
                              Targeting
                            </Table.Cell>
                            <Table.Cell>
                              {wave.targetingMode === 'percentage'
                                ? `${wave.percentage}% of fleet (${deviceIds.length} device${deviceIds.length !== 1 ? 's' : ''})`
                                : `${deviceIds.length} specific device${deviceIds.length !== 1 ? 's' : ''}`}
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell color="gray.500" fontWeight="medium">
                              Devices
                            </Table.Cell>
                            <Table.Cell>
                              <Flex gap={1} wrap="wrap">
                                {deviceIds.map((id) => (
                                  <Tag colorScheme="teal" key={id} size="sm">
                                    {fleetDevices.find((d) => d.id === id)?.name ?? `Device #${id}`}
                                  </Tag>
                                ))}
                              </Flex>
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button isDisabled={isCancellingPending} onClick={handleConfirmSubmit}>
                Confirm &amp; Apply
              </Button>
              <Button onClick={onConfirmClose} variant="outline">
                Cancel
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </Box>
  );
};
